/* eslint-disable react-native/no-color-literals */
import React, { useEffect, useState } from "react";
import {
  Modal,
  Pressable,
  StyleSheet,
  TouchableOpacity,
  View,
  FlatList,
  TextInput,
  ActivityIndicator,
} from "react-native";
import Text from "@components/CustomText";
import Cross from "@assets/svg/CrossIcon";
import colors from "@constants/color";
// eslint-disable-next-line import/no-named-as-default
import t from "@constants/localization";
import toasts from "@utils/toast";
import { PickTpShipment, findShipmentAnywhere } from "./pickTpHelpers";

const PATH = "pickTp";

interface Props {
  visible: boolean;
  pendingShipments: PickTpShipment[];
  scannedCount: number;
  total: number;
  isSubmitting: boolean;
  onCancel: () => void;
  onConfirm: (shipmentIds: string[]) => void;
}

const ItemSeparator = () => <View style={styles.separator} />;

const PickTpManualConfirmModal: React.FC<Props> = ({
  visible,
  pendingShipments,
  scannedCount,
  total,
  isSubmitting,
  onCancel,
  onConfirm,
}) => {
  const [typedId, setTypedId] = useState("");
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (visible) {
      setTypedId("");
      setCheckedIds(new Set());
    }
  }, [visible]);

  const toggleChecked = (shipmentId: string) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(shipmentId)) {
        next.delete(shipmentId);
      } else {
        next.add(shipmentId);
      }
      return next;
    });
  };

  const hasSelection = checkedIds.size > 0 || typedId.trim().length > 0;

  const handleConfirm = () => {
    const ids = new Set(checkedIds);
    const typed = typedId.trim();
    if (typed) {
      const match = findShipmentAnywhere(pendingShipments, typed);
      if (!match) {
        toasts.showShortToast(t(`${PATH}/scanErrorUnknown`));
        return;
      }
      ids.add(match.shipment_id);
    }
    if (ids.size === 0) return;
    onConfirm(Array.from(ids));
  };

  const renderRow = ({ item }: { item: PickTpShipment }) => {
    const checked = checkedIds.has(item.shipment_id);
    return (
      <Pressable
        style={styles.row}
        onPress={() => toggleChecked(item.shipment_id)}
        testID={`pick-tp-manual-tick-${item.shipment_id}`}
      >
        <View style={[styles.checkbox, checked && styles.checkboxChecked]}>
          {checked ? <Text style={styles.checkboxTick}>✓</Text> : null}
        </View>
        <Text variant="body_text_4" style={styles.rowLabel}>
          {item.shipment_id}
        </Text>
      </Pressable>
    );
  };

  return (
    <Modal
      animationType="fade"
      transparent={true}
      visible={visible}
      onRequestClose={onCancel}
      statusBarTranslucent
      navigationBarTranslucent
    >
      <Pressable style={styles.overlay} onPress={onCancel} />

      <View style={styles.centerWrap} pointerEvents="box-none">
        <View style={styles.card}>
          <Pressable style={styles.closeBtn} onPress={onCancel}>
            <Cross width={14} height={14} />
          </Pressable>

          <Text variant="title_20" style={styles.title}>
            {t(`${PATH}/manualConfirmTitle`)}
          </Text>
          <Text variant="caption_2" style={styles.counter}>
            {t(`${PATH}/manualConfirmCounter`, {
              scanned: `${scannedCount}`,
              total: `${total}`,
            })}
          </Text>

          <Text variant="body_text_3" style={styles.subtitle}>
            {t(`${PATH}/manualConfirmSubtitle`)}
          </Text>

          <TextInput
            style={styles.input}
            placeholder={t(`${PATH}/manualConfirmInputPlaceholder`)}
            placeholderTextColor={colors.mediumBlacklightBlack}
            value={typedId}
            onChangeText={setTypedId}
            autoCapitalize="characters"
            autoCorrect={false}
            testID="pick-tp-manual-confirm-input"
          />

          {pendingShipments.length > 0 && (
            <>
              <Text variant="caption_2" style={styles.orLabel}>
                {t(`${PATH}/manualConfirmOr`)}
              </Text>
              <FlatList
                data={pendingShipments}
                keyExtractor={(item) => item.shipment_id}
                renderItem={renderRow}
                ItemSeparatorComponent={ItemSeparator}
                showsVerticalScrollIndicator={false}
                style={styles.list}
              />
            </>
          )}

          <View style={styles.footer}>
            <TouchableOpacity
              style={[styles.footerBtn, styles.cancelBtn]}
              onPress={onCancel}
              disabled={isSubmitting}
              activeOpacity={0.7}
              testID="pick-tp-manual-cancel-btn"
            >
              <Text variant="body_text_2" style={styles.cancelText}>
                {t(`${PATH}/cancel`)}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.footerBtn,
                styles.confirmBtn,
                !hasSelection && styles.confirmBtnDisabled,
              ]}
              onPress={handleConfirm}
              disabled={isSubmitting || !hasSelection}
              activeOpacity={0.7}
              testID="pick-tp-manual-submit-btn"
            >
              {isSubmitting ? (
                <ActivityIndicator size="small" color={colors.white} />
              ) : (
                <Text variant="body_text_2" style={styles.confirmText}>
                  {t(`${PATH}/manualConfirmSubmit`)}
                </Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFill,
    backgroundColor: "rgba(0,0,0,0.4)",
  },
  centerWrap: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 24,
  },
  card: {
    width: "100%",
    backgroundColor: colors.white,
    borderRadius: 16,
    padding: 20,
    maxHeight: "80%",
  },
  closeBtn: {
    position: "absolute",
    right: 10,
    top: 10,
    padding: 8,
    zIndex: 1,
  },
  title: {
    marginBottom: 2,
  },
  counter: {
    color: colors.plain_green,
    marginBottom: 10,
  },
  subtitle: {
    color: colors.mediumBlacklightBlack,
    marginBottom: 14,
  },
  input: {
    borderWidth: 1,
    borderColor: colors.lightMediumBlack,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    color: colors.black,
  },
  orLabel: {
    color: colors.mediumBlacklightBlack,
    textAlign: "center",
    marginVertical: 10,
  },
  list: {
    maxHeight: 180,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 10,
  },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 4,
    borderWidth: 1.5,
    borderColor: colors.lightMediumBlack,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  checkboxChecked: {
    backgroundColor: colors.plain_green,
    borderColor: colors.plain_green,
  },
  checkboxTick: {
    color: colors.white,
    fontSize: 13,
    fontWeight: "700",
  },
  rowLabel: {
    color: colors.mediumBlack,
  },
  separator: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: colors.black02,
  },
  footer: {
    flexDirection: "row",
    gap: 10,
    marginTop: 16,
  },
  footerBtn: {
    flex: 1,
    height: 44,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  cancelBtn: {
    borderWidth: 1,
    borderColor: colors.lightMediumBlack,
    backgroundColor: colors.white,
  },
  confirmBtn: {
    backgroundColor: colors.plain_green,
  },
  confirmBtnDisabled: {
    backgroundColor: colors.silver,
  },
  cancelText: {
    color: colors.black,
  },
  confirmText: {
    color: colors.white,
  },
});

export default PickTpManualConfirmModal;
